import type {
    PlanCoordinates,
    ProjectPlan,
    ProjektVerortung,
    VerortungAchse,
    VerortungGeruest,
    VerortungGeschoss,
    VerortungPasspunkt,
    VerortungPlan,
} from '../../types';
import { geruestLagen, geschossEbenen, type Ebene } from './ebenen';
import { georeferenzBerechnen, type Georeferenz } from './georeferenz';

/**
 * Projekt-Verortung aus dem Blob (`report.metadata.verortung`,
 * docs/verortung-kamera-vertrag.md, Paket A): robust lesen, Georeferenz eines
 * Plans, Plan zur Ebene.
 *
 * Reine Funktionen, nichts wirft.
 */

function standardEinfrieren(): ProjektVerortung {
    const v: ProjektVerortung = { aktiv: true, geschosse: [], geruest: null, plaene: {} };
    Object.freeze(v.geschosse);
    Object.freeze(v.plaene);
    return Object.freeze(v);
}

/**
 * Grundstand: aktiv, nichts eingerichtet. Eingefroren — zum Ändern kopieren
 * (`{ ...STANDARD_VERORTUNG, geschosse: [...] }`); verortungLesen liefert
 * immer eine frische Kopie.
 */
export const STANDARD_VERORTUNG: ProjektVerortung = standardEinfrieren();

const QUELLEN: readonly VerortungPasspunkt['quelle'][] = ['gps', 'utm', 'achse'];

function istObjekt(x: unknown): x is Record<string, unknown> {
    return !!x && typeof x === 'object' && !Array.isArray(x);
}

function endlich(x: unknown): x is number {
    return typeof x === 'number' && Number.isFinite(x);
}

/** −0 als 0 ablegen, sonst zeigt die Anzeige „-0“. */
function ohneMinusNull(x: number): number {
    return x === 0 ? 0 : x;
}

function textGetrimmt(x: unknown): string | null {
    if (typeof x !== 'string') return null;
    const t = x.trim();
    return t === '' ? null : t;
}

function planPunktLesen(roh: unknown): PlanCoordinates | null {
    if (!istObjekt(roh) || !endlich(roh.x) || !endlich(roh.y)) return null;
    return { x: roh.x, y: roh.y };
}

function geschossLesen(roh: unknown): VerortungGeschoss | null {
    if (!istObjekt(roh)) return null;
    const id = textGetrimmt(roh.id);
    const name = textGetrimmt(roh.name);
    if (!id || !name || !endlich(roh.hoeheM)) return null;
    return { id, name, hoeheM: ohneMinusNull(roh.hoeheM) };
}

/** Nur Zahlen prüfen; die Bereiche (Lagen 1–40, Abstand 0,5–5 m) prüft geruestLagen — so geht keine Eingabe verloren. */
function geruestLesen(roh: unknown): VerortungGeruest | null {
    if (!istObjekt(roh)) return null;
    if (!endlich(roh.ersteLageM) || !endlich(roh.abstandM) || !endlich(roh.lagen)) return null;
    return { ersteLageM: ohneMinusNull(roh.ersteLageM), abstandM: roh.abstandM, lagen: roh.lagen };
}

function passpunktLesen(roh: unknown): VerortungPasspunkt | null {
    if (!istObjekt(roh)) return null;
    const id = textGetrimmt(roh.id);
    const plan = planPunktLesen(roh.plan);
    const quelle = QUELLEN.find((q) => q === roh.quelle);
    if (!id || !plan || !quelle || !endlich(roh.ost) || !endlich(roh.nord)) return null;
    const p: VerortungPasspunkt = {
        id,
        name: typeof roh.name === 'string' ? roh.name.trim() : '',
        plan,
        ost: roh.ost,
        nord: roh.nord,
        quelle,
    };
    if (endlich(roh.genauigkeitM) && roh.genauigkeitM >= 0) p.genauigkeitM = roh.genauigkeitM;
    else if (roh.genauigkeitM === null) p.genauigkeitM = null;
    return p;
}

function achseLesen(roh: unknown): VerortungAchse | null {
    if (!istObjekt(roh)) return null;
    const name = textGetrimmt(roh.name);
    const plan = planPunktLesen(roh.plan);
    return name && plan ? { name, plan } : null;
}

function liste<T>(roh: unknown, lesen: (x: unknown) => T | null): T[] {
    if (!Array.isArray(roh)) return [];
    const ergebnis: T[] = [];
    for (const x of roh) {
        const w = lesen(x);
        if (w) ergebnis.push(w);
    }
    return ergebnis;
}

function planLesen(roh: unknown): VerortungPlan {
    const p: VerortungPlan = {};
    if (!istObjekt(roh)) return p;
    if (roh.art === 'geschoss' || roh.art === 'lageplan') p.art = roh.art;
    if (typeof roh.geschossId === 'string') p.geschossId = roh.geschossId;
    else if (roh.geschossId === null) p.geschossId = null;
    if (typeof roh.zone === 'number' && Number.isInteger(roh.zone) && roh.zone >= 1 && roh.zone <= 60) p.zone = roh.zone;
    if (endlich(roh.seitenverhaeltnis) && roh.seitenverhaeltnis > 0) p.seitenverhaeltnis = roh.seitenverhaeltnis;
    if (Array.isArray(roh.passpunkte)) p.passpunkte = liste(roh.passpunkte, passpunktLesen);
    if (Array.isArray(roh.achsen)) p.achsen = liste(roh.achsen, achseLesen);
    return p;
}

/**
 * Blob-Daten robust lesen: fehlend/kaputt → Standard (frische Kopie);
 * ungültige Einträge (Geschoss ohne id/Namen/endliche Höhe, Passpunkt ohne
 * gültige Zahlen, …) fallen weg, doppelte Geschoss-ids zählen einmal (erste),
 * unbekannte Felder werden weggelassen, Namen getrimmt, `aktiv` fehlt → true.
 * Die Reihenfolge der Geschosse bleibt wie gespeichert (sortiert wird in
 * geschossEbenen).
 */
export function verortungLesen(roh: unknown): ProjektVerortung {
    if (!istObjekt(roh)) return { aktiv: true, geschosse: [], geruest: null, plaene: {} };

    const geschosse: VerortungGeschoss[] = [];
    for (const g of liste(roh.geschosse, geschossLesen)) {
        if (!geschosse.some((x) => x.id === g.id)) geschosse.push(g);
    }

    const plaene: Record<string, VerortungPlan> = {};
    if (istObjekt(roh.plaene)) {
        for (const [id, wert] of Object.entries(roh.plaene)) {
            // „__proto__“ als Schlüssel würde den Prototyp setzen statt einen Plan anzulegen.
            if (!id || id === '__proto__' || !istObjekt(wert)) continue;
            plaene[id] = planLesen(wert);
        }
    }

    return {
        aktiv: typeof roh.aktiv === 'boolean' ? roh.aktiv : true,
        geschosse,
        geruest: geruestLesen(roh.geruest),
        plaene,
    };
}

/**
 * Georeferenz eines Plans: null ohne Plan, ohne ≥ 2 Passpunkte, ohne
 * Seitenverhältnis oder ohne Zone; `{ fehler }` bei Rechenfehler
 * (georeferenzBerechnen: Punkte zu nah, vertauscht, ungültig …).
 */
export function planGeoreferenz(plan: VerortungPlan | undefined): Georeferenz | { fehler: string } | null {
    if (!plan || typeof plan !== 'object') return null;
    const punkte = Array.isArray(plan.passpunkte) ? plan.passpunkte : [];
    if (punkte.length < 2) return null;
    if (typeof plan.seitenverhaeltnis !== 'number' || typeof plan.zone !== 'number') return null;
    return georeferenzBerechnen(punkte, plan.seitenverhaeltnis, plan.zone);
}

/** Erfolgreich berechnete Georeferenz (kein null, kein Fehler)? */
function georeferenziert(plan: VerortungPlan | undefined): boolean {
    const g = planGeoreferenz(plan);
    return g !== null && !('fehler' in g);
}

/** Erster passender Plan in der Reihenfolge von `plaene`, georeferenzierte zuerst. */
function bestenWaehlen(v: ProjektVerortung, kandidaten: readonly ProjectPlan[]): ProjectPlan | null {
    if (kandidaten.length === 0) return null;
    return kandidaten.find((p) => georeferenziert(v.plaene?.[p.id])) ?? kandidaten[0];
}

/**
 * Plan zur Ebene: Geschoss → Plan mit art 'geschoss' und passender geschossId
 * (bei mehreren: georeferenzierter zuerst, sonst der erste); Gerüstlage → Plan
 * mit art 'lageplan' (georeferenziert zuerst). Sonst null.
 */
export function planFuerEbene(v: ProjektVerortung, plaene: readonly ProjectPlan[], ebene: Ebene | null): ProjectPlan | null {
    if (!ebene || !v || !Array.isArray(plaene)) return null;
    const kalibrierung = v.plaene ?? {};
    const kandidaten = plaene.filter((p) => {
        if (!p || typeof p.id !== 'string' || !Object.prototype.hasOwnProperty.call(kalibrierung, p.id)) return false;
        const k = kalibrierung[p.id];
        if (ebene.art === 'geschoss') return k?.art === 'geschoss' && k.geschossId === ebene.id;
        return k?.art === 'lageplan';
    });
    return bestenWaehlen(v, kandidaten);
}

/**
 * Ist irgendetwas eingerichtet: gültige Geschosse, ein gültiges Gerüst oder
 * ein georeferenzierter Plan? Letzteres reicht für den Live-Punkt draußen im
 * Rohbau (nur Lage-/BE-Plan kalibriert, noch keine Geschosshöhen).
 */
export function verortungEingerichtet(v: ProjektVerortung): boolean {
    if (!v) return false;
    if (geschossEbenen(v.geschosse ?? []).length > 0 || geruestLagen(v.geruest).length > 0) return true;
    return Object.values(v.plaene ?? {}).some((plan) => {
        const geo = planGeoreferenz(plan);
        return !!geo && !('fehler' in geo);
    });
}
